import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ChevronRight, Download, ExternalLink, Search } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { Empty, ErrorBox } from '@/components/empty';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_authed/api-docs')({
  component: ApiDocsPage,
});

// Minimal OpenAPI 3.x shape — we render manually to keep the chunk small
// and the look consistent with the rest of the admin (no Swagger-UI bundle,
// no CSP exemptions). For full schema introspection, link to /v1/openapi.json.
type OpenAPI = {
  openapi: string;
  info: { title: string; version: string; description?: string };
  paths: Record<string, Record<string, Operation>>;
  components?: { schemas?: Record<string, JsonSchema> };
};

type Operation = {
  tags?: string[];
  summary?: string;
  description?: string;
  operationId?: string;
  parameters?: Param[];
  requestBody?: { content?: Record<string, { schema?: JsonSchema }>; required?: boolean };
  responses?: Record<
    string,
    { description?: string; content?: Record<string, { schema?: JsonSchema }> }
  >;
  security?: unknown[];
};

type Param = {
  name: string;
  in: 'query' | 'path' | 'header' | 'cookie';
  required?: boolean;
  description?: string;
  schema?: JsonSchema;
};

type JsonSchema = {
  type?: string;
  format?: string;
  $ref?: string;
  title?: string;
  enum?: unknown[];
  items?: JsonSchema;
  properties?: Record<string, JsonSchema>;
  anyOf?: JsonSchema[];
  oneOf?: JsonSchema[];
  required?: string[];
};

const METHOD_ORDER = ['get', 'post', 'put', 'patch', 'delete'] as const;

const METHOD_TONE: Record<string, string> = {
  get: 'bg-sky-500/10 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300 border-sky-500/20',
  post: 'bg-emerald-500/10 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300 border-emerald-500/20',
  put: 'bg-amber-500/10 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300 border-amber-500/20',
  patch: 'bg-amber-500/10 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300 border-amber-500/20',
  delete: 'bg-rose-500/10 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300 border-rose-500/20',
};

function ApiDocsPage() {
  const { t } = useTranslation();
  const [filter, setFilter] = React.useState('');

  const spec = useQuery({
    queryKey: ['openapi'],
    queryFn: async () => {
      const r = await fetch('/v1/openapi.json', { credentials: 'omit' });
      if (!r.ok) throw new Error(`openapi.json: ${r.status}`);
      return (await r.json()) as OpenAPI;
    },
    staleTime: 5 * 60_000,
  });

  const groups = React.useMemo(() => groupByTag(spec.data, filter), [spec.data, filter]);

  return (
    <div className="space-y-6">
      <header className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div className="space-y-1">
          <h1 className="text-3xl font-semibold tracking-tight">{t('api_docs.title')}</h1>
          <p className="text-sm text-muted-foreground">
            {t('api_docs.subtitle')}{' '}
            {spec.data ? (
              <Badge variant="secondary">{spec.data.info.version}</Badge>
            ) : (
              <span className="opacity-50">…</span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <a
            href="/v1/openapi.json"
            download="payhub-openapi.json"
            className="inline-flex items-center gap-1.5 rounded-md border border-input bg-card px-3 py-1.5 text-sm font-medium hover:bg-accent hover:text-accent-foreground"
          >
            <Download className="h-3.5 w-3.5" /> {t('api_docs.download_json')}
          </a>
          <a
            href="/v1/docs"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-md border border-input bg-card px-3 py-1.5 text-sm font-medium hover:bg-accent hover:text-accent-foreground"
          >
            <ExternalLink className="h-3.5 w-3.5" /> {t('api_docs.open_swagger')}
          </a>
        </div>
      </header>

      <div className="relative max-w-md">
        <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('api_docs.search_placeholder') as string}
          className="ps-9"
        />
      </div>

      {spec.isLoading && <Empty title={t('common.loading')} />}
      {spec.isError && <ErrorBox message={(spec.error as Error).message} />}

      {spec.data && groups.length === 0 && <Empty title={t('api_docs.no_match')} />}

      <div className="space-y-6">
        {groups.map((g) => (
          <section key={g.tag} className="space-y-3">
            <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              {g.tag}
              <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium normal-case tracking-normal text-muted-foreground">
                {g.ops.length}
              </span>
            </h2>
            <div className="space-y-2">
              {g.ops.map((op) => (
                <OperationRow key={`${op.method}:${op.path}`} op={op} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

type FlatOp = {
  method: string;
  path: string;
  op: Operation;
};

function groupByTag(spec: OpenAPI | undefined, filter: string) {
  if (!spec) return [];
  const q = filter.trim().toLowerCase();
  const buckets = new Map<string, FlatOp[]>();

  for (const [path, methods] of Object.entries(spec.paths)) {
    for (const method of METHOD_ORDER) {
      const op = methods[method];
      if (!op) continue;
      const tags = op.tags && op.tags.length > 0 ? op.tags : ['untagged'];
      const matches =
        !q ||
        path.toLowerCase().includes(q) ||
        op.summary?.toLowerCase().includes(q) ||
        op.operationId?.toLowerCase().includes(q) ||
        tags.some((t) => t.toLowerCase().includes(q));
      if (!matches) continue;
      for (const tag of tags) {
        const arr = buckets.get(tag) ?? [];
        arr.push({ method, path, op });
        buckets.set(tag, arr);
      }
    }
  }

  return Array.from(buckets.entries())
    .map(([tag, ops]) => ({
      tag,
      ops: ops.sort((a, b) => a.path.localeCompare(b.path) || a.method.localeCompare(b.method)),
    }))
    .sort((a, b) => a.tag.localeCompare(b.tag));
}

function OperationRow({ op: { method, path, op } }: { op: FlatOp }) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  return (
    <Card>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-3 p-4 text-start focus-ring rounded-xl"
        aria-expanded={open}
      >
        <span
          className={cn(
            'inline-flex h-6 min-w-[58px] items-center justify-center rounded border px-1.5 text-[11px] font-bold uppercase tracking-wider',
            METHOD_TONE[method] ?? 'bg-muted text-foreground border-border',
          )}
        >
          {method}
        </span>
        <code className="font-mono text-sm text-foreground">{path}</code>
        {op.summary && (
          <span className="ms-2 truncate text-sm text-muted-foreground">— {op.summary}</span>
        )}
        <ChevronRight
          className={cn(
            'ms-auto h-4 w-4 shrink-0 text-muted-foreground transition-transform',
            open && 'rotate-90',
          )}
        />
      </button>
      {open && (
        <CardContent className="border-t border-border pt-4 text-sm">
          {op.description && (
            <p className="mb-4 whitespace-pre-wrap text-muted-foreground">{op.description}</p>
          )}
          <ParamSection title="path" params={(op.parameters ?? []).filter((p) => p.in === 'path')} />
          <ParamSection title="query" params={(op.parameters ?? []).filter((p) => p.in === 'query')} />
          <ParamSection title="headers" params={(op.parameters ?? []).filter((p) => p.in === 'header')} />
          {op.requestBody && (
            <DefList title={t('api_docs.request_body') as string}>
              {Object.entries(op.requestBody.content ?? {}).map(([ct, body]) => (
                <Row key={ct} label={ct}>
                  <SchemaRef schema={body.schema} />
                  {op.requestBody?.required && <RequiredPill />}
                </Row>
              ))}
            </DefList>
          )}
          {op.responses && (
            <DefList title={t('api_docs.responses') as string}>
              {Object.entries(op.responses).map(([code, resp]) => (
                <Row key={code} label={code}>
                  <span className="text-foreground">{resp.description ?? '—'}</span>
                  {resp.content && Object.keys(resp.content).length > 0 && (
                    <span className="text-xs text-muted-foreground">
                      {Object.keys(resp.content).join(', ')}
                    </span>
                  )}
                </Row>
              ))}
            </DefList>
          )}
        </CardContent>
      )}
    </Card>
  );
}

function ParamSection({ title, params }: { title: string; params: Param[] }) {
  if (params.length === 0) return null;
  return (
    <DefList title={title}>
      {params.map((p) => (
        <Row key={`${p.in}:${p.name}`} label={p.name}>
          <code className="font-mono text-xs text-muted-foreground">{schemaSummary(p.schema)}</code>
          {p.required && <RequiredPill />}
          {p.description && <span className="text-muted-foreground">{p.description}</span>}
        </Row>
      ))}
    </DefList>
  );
}

function DefList({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-4 last:mb-0">
      <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </h4>
      <dl className="divide-y divide-border rounded-md border border-border bg-muted/30">
        {children}
      </dl>
    </section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid items-baseline gap-2 px-3 py-2 sm:grid-cols-[160px_1fr] sm:gap-4">
      <dt className="font-mono text-xs text-foreground">{label}</dt>
      <dd className="flex flex-wrap items-center gap-2 text-sm">{children}</dd>
    </div>
  );
}

function RequiredPill() {
  const { t } = useTranslation();
  return (
    <span className="rounded-full bg-rose-500/10 px-1.5 py-0.5 text-[10px] font-medium text-rose-600 dark:text-rose-300">
      {t('common.required')}
    </span>
  );
}

function SchemaRef({ schema }: { schema?: JsonSchema }) {
  if (!schema) return <span className="text-muted-foreground">—</span>;
  if (schema.$ref) {
    const name = schema.$ref.split('/').pop();
    return <code className="font-mono text-xs text-primary">{name}</code>;
  }
  return <code className="font-mono text-xs text-foreground">{schemaSummary(schema)}</code>;
}

function schemaSummary(s?: JsonSchema): string {
  if (!s) return '—';
  if (s.$ref) return s.$ref.split('/').pop() ?? '';
  if (s.anyOf || s.oneOf) {
    const parts = (s.anyOf ?? s.oneOf ?? []).map((x) => schemaSummary(x));
    return parts.join(' | ');
  }
  if (s.type === 'array') return `${schemaSummary(s.items)}[]`;
  if (s.enum) return s.enum.map((e) => JSON.stringify(e)).join(' | ');
  if (s.format) return `${s.type ?? 'unknown'} (${s.format})`;
  return s.type ?? s.title ?? 'unknown';
}
